import { useEffect, useState } from "react";
import { useCustomKeycloak } from "../../hooks/useCustomKeycloak";
import { useEndpoints } from "../../hooks/useEndpoints";
import BackgroundPage from "../../shared/BackgroundPage/BackgroundPage";
import CustomCard from "../../shared/Card/CustomCard";
import CustomAvatar from "../../shared/CustomAvatar/CustomAvatar";
import Typhography from "../../shared/Typhography/Typhography";
import style from "./SelectEntityPage.module.scss";
import LeopartLoader from "../../shared/LeopartLoader/LeopartLoader";
import {
  CaretRightIcon,
  SirenIcon,
  SwatchesIcon,
  UserCircleIcon,
  UsersIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import { useAppDispatch, useAppSelector } from "../../store";
import { setEntities, setSelectedEntity } from "../../store/reducers/me";
import { getStorageItem } from "../../utils/localStorage";
import { IUser2 } from "../../models/user copy";
import { USER_TYPES } from "../../enums/user";
import { GenericErrorPage } from "../GenericErrorPage/GenericErrorPage";
import { NotAllowedUserPage } from "../NotAllowedUserPage/NotAllowedUserPage";
import LoadingPage from "../../shared/LoadingPage/LoadingPage";
import { getLabelFromOptions } from "../../lib/DurationPicker/utils";
import { USER_TYPE_OPTIONS } from "../../constants/config";
import { IDirector } from "../../models/director";
import { IUserRappresentation } from "../../models/userRappresentation";

export type EntitiesResponse = {
  directors: IDirector[];
  users: IUser2<IDirector>[];
  athletes: IUser2<IDirector>[];
  user: IUserRappresentation;
};
export const SelectEntityPage = () => {
  const { keycloak } = useCustomKeycloak();
  const dispatch = useAppDispatch();
  const entities = useAppSelector((st) => st.me.entities);
  const [isLoading, setISLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<EntitiesResponse | undefined>(entities);
  const { getEntities } = useEndpoints();

  const fetchEntities = async () => {
    setISLoading(true);
    try {
      const lastSelectedEntity = getStorageItem("selectedEntity");

      const id = lastSelectedEntity?._id;

      const { data: response } = await getEntities(keycloak.tokenParsed!.email);
      const directors = response.directors;
      const { users, athletes } = response.users.reduce(
        (acc, curr) => {
          if (curr.type === USER_TYPES.ATHLETE) {
            acc.athletes.push(curr);
          } else {
            acc.users.push(curr);
          }
          return acc;
        },
        {
          athletes: [] as IUser2<IDirector>[],
          users: [] as IUser2<IDirector>[],
        }
      );
      const matchLastDirector = directors?.find(({ _id }) => _id === id);
      const matchLastUser = users?.find(({ _id }) => _id === id);

      if (matchLastDirector) {
        dispatch(setSelectedEntity(matchLastDirector));
      } else if (matchLastUser) {
        dispatch(setSelectedEntity(matchLastUser as any));
      } else if (directors?.length === 1 && !users?.length) {
        dispatch(setSelectedEntity(directors[0]));
      } else if (users?.length === 1 && !directors?.length) {
        dispatch(setSelectedEntity(users[0] as any));
      } else if (id) {
        // selectedEntity presente in localStorage ma non appartiene a
        // nessuna delle entity del login corrente (tipico di cambio
        // account senza logout esplicito): resetta per forzare la
        // selezione esplicita dal picker.
        dispatch(setSelectedEntity(undefined));
      }
      dispatch(
        setEntities({
          directors,
          users,
          athletes,
          user: response.user,
        })
      );

      setData({ directors, users, athletes, user: response.user });
    } catch (error) {
      setError(true);
    } finally {
      setTimeout(() => {
        setISLoading(false);
      }, 40);
    }
  };

  useEffect(() => {
    !entities && fetchEntities();
    if (entities) setISLoading(false);
  }, []);

  if (isLoading) return <LoadingPage />;
  if (error) {
    return (
      <GenericErrorPage
        Icon={WarningIcon}
        title="Errore"
        description="Errore caricamento pagina"
      />
    );
  }
  if (
    !data?.directors.length &&
    !data?.users.length &&
    !data?.athletes.length
  ) {
    return (
      <GenericErrorPage
        Icon={SirenIcon}
        title="Non hai utenze attive"
        description="Non hai utenze attive in questo momento come atleta"
      />
    );
  }

  if (!data?.directors.length && !data?.users.length && data?.athletes.length) {
    return <NotAllowedUserPage />;
  }

  const getInitials = (name: string) => {
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <>
      <BackgroundPage />
      <div className="container__dashboard__page h-100">
        <div className="d-flex align-items-center justify-content-center w-100 h-100">
          <CustomCard className={style.containerCard}>
            {isLoading ? (
              <LeopartLoader />
            ) : (
              <>
                <div className={style.logo}>
                  <SwatchesIcon weight="fill" />
                </div>
                <div className={style.header}>
                  <Typhography size="bold-20" className={style.title}>
                    Seleziona ambiente
                  </Typhography>
                  <Typhography size="regular-14" className={style.subtitle}>
                    Scegli con quale account vuoi accedere
                  </Typhography>
                </div>

                {!!data?.directors.length && (
                  <div className={style.section}>
                    <Typhography size="bold-12" className={style.sectionLabel}>
                      <UsersIcon size={12} style={{ marginRight: 4 }} />
                      Direttore
                    </Typhography>
                    {data.directors.map((director) => (
                      <div
                        className={style.containerEntity}
                        key={director._id}
                        onClick={() => dispatch(setSelectedEntity(director))}
                      >
                        <CustomAvatar
                          name={getInitials(director.name)}
                          src={director.image ? `${process.env.REACT_APP_BASE_URL_IMAGE}${director.image}` : undefined}
                          className={style.avatar}
                        />
                        <div className={style.entityInfo}>
                          <Typhography size="bold-14" className={style.entityName}>
                            {director.name}
                          </Typhography>
                          <Typhography size="regular-12" className={style.entityRole}>
                            Direttore sportivo
                          </Typhography>
                        </div>
                        <CaretRightIcon className={style.icon} size={16} />
                      </div>
                    ))}
                  </div>
                )}

                {!!data?.directors.length && !!data?.users.length && (
                  <div className={style.divider} />
                )}

                {!!data?.users.length && (
                  <div className={style.section}>
                    <Typhography size="bold-12" className={style.sectionLabel}>
                      <UserCircleIcon size={12} style={{ marginRight: 4 }} />
                      Utente
                    </Typhography>
                    {data.users.map((user) => (
                      <div
                        className={style.containerEntity}
                        key={user._id}
                        onClick={() => dispatch(setSelectedEntity(user as any))}
                      >
                        <CustomAvatar
                          name={getInitials(user.directorId.name)}
                          src={user.image ? `${process.env.REACT_APP_BASE_URL_IMAGE}${user.image.image}` : undefined}
                          className={style.avatar}
                        />
                        <div className={style.entityInfo}>
                          <Typhography size="bold-14" className={style.entityName}>
                            {user.directorId.name}
                          </Typhography>
                          <Typhography size="regular-12" className={style.entityRole}>
                            {getLabelFromOptions(USER_TYPE_OPTIONS, user.type)}
                          </Typhography>
                        </div>
                        <CaretRightIcon className={style.icon} size={16} />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </CustomCard>
        </div>
      </div>
    </>
  );
};
